import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

const TABLE_FILTERS_ID = 'table-filters';

export interface UseStickyTableHeaderWithHorizontalScrollOptions {
  isTablet?: boolean;
  /** Pass to recalculate header top offset when filters container height changes */
  forceRecalculateTableFiltersHeight?: unknown;
}

export interface UseStickyTableHeaderWithHorizontalScrollReturn {
  /** Attach to the scrollable body container (element with overflow-x: auto) */
  bodyScrollRef: RefObject<HTMLDivElement | null>;
  /** Attach to the inner wrapper of the sticky header content (same width as table) */
  stickyHeaderInnerRef: RefObject<HTMLDivElement | null>;
  /** Pass to TableHeader as fixedColumnRef so the fixed column stays aligned */
  fixedColumnRef: RefObject<HTMLDivElement | null>;
  /** Height of #table-filters element; use for sticky top offset */
  tableFiltersHeight: number;
  /** Attach to body scroll container's onScroll */
  onBodyScroll: () => void;
}

/**
 * Hook for tables that need both a sticky header (on vertical scroll) and horizontal scroll.
 * Because position: sticky does not work inside an overflow-x container, the header is
 * rendered outside the scroll container and its horizontal position is synced via refs
 * and transform (no React state) for smooth scrolling.
 */
export function useStickyTableHeaderWithHorizontalScroll(
  options: UseStickyTableHeaderWithHorizontalScrollOptions = {}
): UseStickyTableHeaderWithHorizontalScrollReturn {
  const { isTablet, forceRecalculateTableFiltersHeight } = options;

  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const stickyHeaderInnerRef = useRef<HTMLDivElement>(null);
  const fixedColumnRef = useRef<HTMLDivElement>(null);
  const [tableFiltersHeight, setTableFiltersHeight] = useState(0);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    const measure = () => {
      const el = document.getElementById(TABLE_FILTERS_ID);
      setTableFiltersHeight(el?.offsetHeight ?? 0);
    };

    const scheduleMeasure = () => {
      if (rafIdRef.current !== null) return;
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        measure();
      });
    };

    measure();
    /** Re-measure in case #table-filters appears in DOM after mount (e.g. conditional/lazy render) */
    const timeoutId = setTimeout(measure, 1000);

    const el = document.getElementById(TABLE_FILTERS_ID);
    const resizeObserver =
      el && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleMeasure)
        : null;
    if (resizeObserver && el) {
      resizeObserver.observe(el);
    }

    window.addEventListener('resize', scheduleMeasure);

    return () => {
      clearTimeout(timeoutId);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      resizeObserver?.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [isTablet, forceRecalculateTableFiltersHeight]);

  const syncHeaderScroll = useCallback(() => {
    const scrollLeft = bodyScrollRef.current?.scrollLeft ?? 0;
    const headerInner = stickyHeaderInnerRef.current;
    const fixedCol = fixedColumnRef.current;
    if (headerInner) {
      headerInner.style.transform = `translateX(-${scrollLeft}px)`;
    }
    if (fixedCol) {
      fixedCol.style.transform = `translateX(${scrollLeft}px)`;
    }
  }, []);

  useEffect(() => {
    const rafId = requestAnimationFrame(syncHeaderScroll);
    return () => cancelAnimationFrame(rafId);
  }, [syncHeaderScroll]);

  return {
    bodyScrollRef,
    stickyHeaderInnerRef,
    fixedColumnRef,
    tableFiltersHeight,
    onBodyScroll: syncHeaderScroll
  };
}
